import React, { Fragment, useEffect, useState } from "react";
import { Col, Form, Row, Button, Card, ProgressBar } from "react-bootstrap";
import { CCArticleResult, ReEntryAssessmentResult } from "../Models/Assessment";
import { ArticleResultList } from "../ContinuingCertification/Components/ArticleResultList";
import { configService } from "../../../Services/Config/ConfigService";
import { getDateWithOffset } from "../../Shared/Utility/GetDateWithOffset";

interface IProps {
    assessment: ReEntryAssessmentResult;
}

export function ReEntryAssessmentCard({ assessment }: IProps) {
    const [detailVisible, setDetailVisible] = useState<boolean>(false);
    const [articleAccessUrl, setArticleAccessUrl] = useState<string>("");

    useEffect(() => {
        (async () => {
            const cfg = (await configService.getConfiguration()).ArticleAccessUrl;
            setArticleAccessUrl(cfg);
        })();
    }, []);

    // Flattened results for single certification
    const results: CCArticleResult[] = assessment.ccArticleResults || [];

    const getTotalPassed = (): number =>
        results.reduce((count, r) => (r.isPass ? count + 1 : count), 0);

    const getTotalFailed = (): number =>
        results.reduce((count, r) => (!r.isPass ? count + 1 : count), 0);

    const failureMax = assessment.attemmtpsAllowed - assessment.totalArticleRequirement;
    const percentPassed = (getTotalPassed() / assessment.totalArticleRequirement) * 100;
    const percentFailed = failureMax > 0 ? (getTotalFailed() / failureMax) * 100 : 0;
    const totalAttempts = getTotalPassed() + getTotalFailed();

    const getStatusMessage = (): string => {
        const passed = getTotalPassed();
        const failed = getTotalFailed();
        if (failed > failureMax) {
            return (
                "You have exceeded the number of failed article exams allowed. " +
                "Unfortunately, you were unsuccessful with the article exams. " +
                "You will need to apply/pay for, and pass, the traditional CC/recertification exam."
            );
        } else if (passed < assessment.totalArticleRequirement) {
            const remaining = assessment.totalArticleRequirement - passed;
            return `You have passed ${passed} of ${assessment.totalArticleRequirement} article exams. ${remaining} remaining.`;
        } else {
            return `Congratulations! You have passed ${passed} of ${assessment.totalArticleRequirement} article exams needed to pass.`;
        }
    };

    const getFormattedDueDate = (): string =>
        getDateWithOffset(assessment.dueDate).toLocaleDateString();

    return (
        <Fragment>
            <Row className="align-items-end">
                <Col>
                    <Form.Group controlId="dueDate">
                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">
                            Article Exams Due By
                        </Form.Label>
                        <Form.Text>{getFormattedDueDate()}</Form.Text>
                    </Form.Group>
                </Col>
            </Row>

            <Row noGutters className="align-items-end">
                <Col className="text-center col-2">
                    <Form.Label className="mb-0 font-weight-bold pp-font-size-smaller">
                        {assessment.certificationName}
                    </Form.Label>
                </Col>
                <Col className="text-center col-4">
                    <Form.Label className="mb-0 font-weight-bold pp-font-size-smaller">
                        Passed
                    </Form.Label>
                </Col>
                <Col className="text-center col-4">
                    <Form.Label className="mb-0 font-weight-bold pp-font-size-smaller">
                        Failed
                    </Form.Label>
                </Col>
                <Col className="text-center col-2">
                    <Form.Label className="mb-0 font-weight-bold pp-font-size-smaller">
                        Total Attempts
                    </Form.Label>
                </Col>
            </Row>

            <Row noGutters className="align-items-center mb-0">
                <Col className="text-left col-2">
                    {/* Empty spacer beneath certification name above */}
                </Col>
                <Col className="col-4 text-right" style={{ height: "26px" }}>
                    <div className="d-flex flex-row">
                        <div className="w-75 mb-1 mt-1">
                            <ProgressBar
                                variant="pp-secondary"
                                now={percentPassed}
                                max={100}
                            />
                        </div>
                        <Form.Text className="ml-1">
                            {getTotalPassed()}/{assessment.totalArticleRequirement}
                        </Form.Text>
                    </div>
                </Col>
                <Col className="col-4 text-right" style={{ height: "26px" }}>
                    <div className="d-flex flex-row">
                        <div className="w-75 mb-1 mt-1">
                            <ProgressBar
                                variant="pp-secondary"
                                now={percentFailed}
                                max={100}
                            />
                        </div>
                        <Form.Text className="ml-1">
                            {getTotalFailed()}/{failureMax}
                        </Form.Text>
                    </div>
                </Col>
                <Col className="text-center col-2">
                    <Form.Label className="mb-0 pp-font-size-smaller">
                        {totalAttempts}/{assessment.attemmtpsAllowed}
                    </Form.Label>
                </Col>
            </Row>

            <Row noGutters>
                <Col className="text-center col-11">
                    <Form.Label className="font-weight-bolder mt-2 pp-font-size-smaller">
                        {getStatusMessage()}
                    </Form.Label>
                </Col>
            </Row>

            {detailVisible && (
                <Row>
                    <ArticleResultList articleResults={results} />
                </Row>
            )}

            <Card bsPrefix="border-0 pp-align-self-end mt-2">
                <a href={articleAccessUrl} target="_blank">
                    <Button variant="pp-primary" className="mb-3 mr-1">
                        Access Article Exams
                        <i className="ml-1 las la-external-link-alt" />
                    </Button>
                </a>
                {results.length > 0 && (
                    <Button
                        className="mb-3"
                        variant="outline-pp-primary"
                        onClick={() => setDetailVisible(!detailVisible)}
                    >
                        {detailVisible ? "Hide" : "Show"} Detail
                    </Button>
                )}
            </Card>

            <div className="pp-font-size-smallest">
                * Clicking 'Access Article Exams' will navigate you to the article portal
            </div>

            <hr className="mt-0" />
        </Fragment>
    );
}

export default ReEntryAssessmentCard;
